<template>
  <div class="animated">
    <b-card>
      <div slot="header">
        <i class="icon-speech"></i> Triggers
        <span
          v-if="limit > 0"
          class="usage-chip"
          :class="{ 'usage-chip--warn': usagePct >= 80, 'usage-chip--full': data.length >= limit }"
          :title="data.length + ' of ' + limit + ' triggers used'"
        >
          {{ data.length }} / {{ limit }}
        </span>
      </div>
      <b-button
        variant="success"
        v-on:click="openCreateModal"
        :disabled="data.length >= limit"
      >
        <font-awesome-icon icon="plus" style="margin-right: 5px" />Create new
        trigger
      </b-button>
      <br />
      <br />
      <div
        v-if="!isPremium && limit > 0 && usagePct >= 80"
        class="trigger-upsell"
        :class="{ 'trigger-upsell--full': data.length >= limit }"
      >
        <div class="trigger-upsell__icon"><i class="fas fa-crown"></i></div>
        <div class="trigger-upsell__text">
          <strong v-if="data.length >= limit">You've hit your trigger limit.</strong>
          <strong v-else>You're running out of triggers.</strong>
          <span>Premium unlocks more triggers, longer responses and advanced TagScript.</span>
        </div>
        <a href="/get-premium" class="trigger-upsell__cta">
          Get Premium <i class="fas fa-arrow-right"></i>
        </a>
      </div>
      <hr />
      <div v-if="loading">
        <div class="sk-circle-fade float-left">
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
        </div>
        <h2 class="float-left ml-4">Loading triggers...</h2>
      </div>
      <div v-else>
        <v-client-table
          :columns="columns"
          :data="data"
          :options="options"
          :theme="theme"
          id="dataTable"
          style="width: 100%; margin: none"
        >
          <p
            slot="response"
            class="d-none d-md-inline-block"
            slot-scope="props"
            style="max-width: 780px !important"
            block
          >
            {{ props.row.response }}
            <b-badge
              v-if="Object.keys(JSON.parse(props.row.embed)).length > 1"
              class="ml-2"
              variant="primary"
              pill
              >embed</b-badge
            >
            <!-- Show role filtering badges for multiple roles -->
            <span v-if="props.row.roles_wl && props.row.roles_wl.length > 0">
              <b-badge
                v-for="role_id in props.row.roles_wl"
                :key="`wl-${role_id}`"
                class="ml-1"
                variant="success"
                pill
                >WL: {{ getRoleName(role_id) }}</b-badge
              >
            </span>
            <span v-if="props.row.roles_bl && props.row.roles_bl.length > 0">
              <b-badge
                v-for="role_id in props.row.roles_bl"
                :key="`bl-${role_id}`"
                class="ml-1"
                variant="danger"
                pill
                >BL: {{ getRoleName(role_id) }}</b-badge
              >
            </span>
            <!-- Show channel filtering badges -->
            <span
              v-if="props.row.channel_wl && props.row.channel_wl.length > 0"
            >
              <b-badge
                v-for="channel_id in props.row.channel_wl"
                :key="`ch-wl-${channel_id}`"
                class="ml-1"
                variant="info"
                pill
                >CH-WL: {{ getChannelName(channel_id) }}</b-badge
              >
            </span>
            <span
              v-if="props.row.channel_bl && props.row.channel_bl.length > 0"
            >
              <b-badge
                v-for="channel_id in props.row.channel_bl"
                :key="`ch-bl-${channel_id}`"
                class="ml-1"
                variant="warning"
                pill
                >CH-BL: {{ getChannelName(channel_id) }}</b-badge
              >
            </span>
          </p>
          <p slot="type" slot-scope="props" block>
            {{ matchtypes[props.row.match_type - 1] }}
          </p>
          <b-button
            slot="actions"
            slot-scope="props"
            block
            variant="outline-primary"
            v-on:click="openEditModal(props.row)"
            >Edit</b-button
          >
        </v-client-table>
      </div>
    </b-card>

    <!-- EDIT MODAL -->
    <b-modal
      size="lg"
      v-model="largeModal"
      @ok="largeModal = false"
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <i class="icon-speech" style="margin-right: 10px"></i>Edit Trigger
        </h4>
      </template>
      <b-form-group>
        <!-- Match Type at the top -->
        <h4 class="smalltitle">Match Type</h4>
        <b-form-select
          id="server_channel_select"
          v-model="trigger_type"
          value="Please select"
        >
          <option v-for="t in match_mapping" :key="t.id" :value="t.id">
            {{ t.name }}
          </option>
        </b-form-select>
        <hr />

        <!-- Trigger Input - Hide for Role Assignment Triggers -->
        <div v-if="trigger_type !== 8">
          <h4 class="smalltitle">Trigger</h4>
          <b-input-group>
            <b-form-input
              type="text"
              pattern="[A-z0-9-_ ]{1,50}"
              class="col-12"
              :disabled="true"
              v-model="trigger_name"
            ></b-form-input>
          </b-input-group>
          <hr />
        </div>

        <!-- Channel for Role Assignment Triggers (match_type = 8) -->
        <div v-if="trigger_type === 8">
          <h4 class="smalltitle">Target Channel</h4>
          <b-form-select
            v-model="role_assignment_channel"
            value="Please select"
            style="margin-bottom: 15px"
          >
            <option :value="null" disabled>Please select a channel</option>
            <option
              v-for="c in channels.filter((ch) => ch.type === 0)"
              :key="c.id"
              :value="c.id"
            >
              #{{ c.name }}
            </option>
          </b-form-select>
          <hr />
        </div>

        <h4 class="smalltitle">Response</h4>
        <b-input-group>
          <b-form-textarea
            rows="6"
            type="text"
            placeholder="Enter some trigger content"
            class="col-12"
            v-model="trigger_content"
          ></b-form-textarea>
        </b-input-group>
        <EmbedBuilder ref="embed_builder_edit"></EmbedBuilder>
        <hr />

        <!-- Universal Role Filtering - For ALL trigger types -->

        <div style="margin-bottom: 15px">
          <h5 class="smalltitle">Whitelist Roles</h5>
          <multiselect
            v-model="roles_wl"
            :options="guild_roles"
            :multiple="true"
            label="name"
            track-by="id"
            placeholder="Select roles"
            style="margin-bottom: 10px"
          >
            <template slot="tag" slot-scope="{ option, search, remove }">
              <span
                class="multiselect__tag"
                :style="{
                  'border-color': option
                    ? '#' + option.color.toString(16)
                    : '#eeaaee',
                }"
              >
                <i
                  aria-hidden="true"
                  tabindex="1"
                  @keydown.enter.prevent="remove(option)"
                  @mousedown.prevent="remove(option)"
                  class="multiselect__tag-icon fakediscord"
                  :style="{
                    'background-color': '#' + intToHex(option.color || 0),
                  }"
                ></i>
                <span class="defaultcursor" v-text="option.name"></span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div
                :style="{ color: parseColor(props.option) }"
                class="option__desc"
              >
                <span class="option__title">{{ props.option.name }}</span>
              </div>
            </template>
          </multiselect>

          <h5 class="smalltitle">Blacklist Roles</h5>
          <multiselect
            v-model="roles_bl"
            :options="guild_roles"
            :multiple="true"
            label="name"
            track-by="id"
            placeholder="Select roles"
            style="margin-bottom: 10px"
          >
            <template slot="tag" slot-scope="{ option, search, remove }">
              <span
                class="multiselect__tag"
                :style="{
                  'border-color': option
                    ? '#' + option.color.toString(16)
                    : '#eeaaee',
                }"
              >
                <i
                  aria-hidden="true"
                  tabindex="1"
                  @keydown.enter.prevent="remove(option)"
                  @mousedown.prevent="remove(option)"
                  class="multiselect__tag-icon fakediscord"
                  :style="{
                    'background-color': '#' + intToHex(option.color || 0),
                  }"
                ></i>
                <span class="defaultcursor" v-text="option.name"></span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div
                :style="{ color: parseColor(props.option) }"
                class="option__desc"
              >
                <span class="option__title">{{ props.option.name }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <hr />

        <!-- Universal Channel Filtering - For ALL trigger types except specific channel -->
        <div v-if="trigger_type !== 3 && trigger_type !== 8" style="margin-bottom: 15px">
          <h5 class="smalltitle">Whitelist Channels</h5>
          <multiselect
            v-model="channels_wl"
            :options="groupedChannels"
            :multiple="true"
            group-values="channels"
            group-label="category"
            :group-select="false"
            label="name"
            track-by="id"
            id="edit_channel_wl"
          >
            <template slot="tag" slot-scope="{ option, search, remove }">
              <span
                class="multiselect__tag channelpicker__tag"
                :style="{ borderColor: '#eee' }"
              >
                <i
                  aria-hidden="true"
                  tabindex="1"
                  @keydown.enter.prevent="remove(option)"
                  @mousedown.prevent="remove(option)"
                  class="multiselect__tag-icon channelpicker_icon"
                  :style="{ backgroundColor: '#eee' }"
                ></i>
                <font-awesome-icon
                  style="color: #999; margin-right: 3px"
                  icon="hashtag"
                />
                <span class="defaultcursor" v-text="option.name"></span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="option__title">
                  <font-awesome-icon
                    style="color: #72767d"
                    :icon="
                      !props.option['$isLabel'] ? 'hashtag' : 'chevron-down'
                    "
                  />
                  {{ props.option.name || props.option.$groupLabel.name }}
                </span>
              </div>
            </template>
          </multiselect>

          <h5 class="smalltitle" style="margin-top: 10px">
            Blacklist Channels
          </h5>
          <multiselect
            v-model="channels_bl"
            :options="groupedChannels"
            :multiple="true"
            group-values="channels"
            group-label="category"
            :group-select="false"
            label="name"
            track-by="id"
            id="edit_channel_bl"
          >
            <template slot="tag" slot-scope="{ option, search, remove }">
              <span
                class="multiselect__tag channelpicker__tag"
                :style="{ borderColor: '#eee' }"
              >
                <i
                  aria-hidden="true"
                  tabindex="1"
                  @keydown.enter.prevent="remove(option)"
                  @mousedown.prevent="remove(option)"
                  class="multiselect__tag-icon channelpicker_icon"
                  :style="{ backgroundColor: '#eee' }"
                ></i>
                <font-awesome-icon
                  style="color: #999; margin-right: 3px"
                  icon="hashtag"
                />
                <span class="defaultcursor" v-text="option.name"></span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="option__title">
                  <font-awesome-icon
                    style="color: #72767d"
                    :icon="
                      !props.option['$isLabel'] ? 'hashtag' : 'chevron-down'
                    "
                  />
                  {{ props.option.name || props.option.$groupLabel.name }}
                </span>
              </div>
            </template>
          </multiselect>
          <hr />
        </div>

        <b-form-group>
          <h5 class="smalltitle" v-if="trigger_type === 3">Specific Channel</h5>
          <!-- Channel Specific (Original) - Only for match_type = 3 -->
          <b-form-select
            id="server_channel_select"
            v-model="trigger_channel"
            value="Please select"
            v-if="trigger_type === 3"
            style="margin-top: 10px"
          >
            <option v-for="c in channels" :key="c.id" :value="c.id">
              #{{ c.name }}
            </option>
          </b-form-select>

        </b-form-group>
      </b-form-group>
      <div slot="modal-footer" class="w-100">
        <b-container
          fluid
          class="text-light text-center"
          style="padding-right: 0px"
        >
          <b-row class="mr-auto">
            <b-col cols="auto" class="mr-auto p-2">
              <b-button
                v-b-tooltip.hover
                :title="
                  trigger_id != undefined
                    ? 'Delete trigger'
                    : 'Refresh the page if you wish to delete this trigger'
                "
                variant="ghost-danger"
                :disabled="trigger_id == undefined"
                v-on:click="deleteTrigger"
              >
                <i class="icon-trash"></i>
              </b-button>
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button
                variant="ghost-secondary"
                v-on:click="largeModal = false"
                >Cancel</b-button
              >
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="primary" v-on:click="editTrigger"
                >Save</b-button
              >
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-modal>

    <!-- CREATE MODAL -->
    <b-modal
      size="lg"
      v-model="createModal"
      @ok="xd = false"
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <i class="icon-speech" style="margin-right: 10px"></i>Create Trigger
        </h4>
      </template>
      <b-form-group>
        <!-- Match Type at the top -->
        <h4 class="smalltitle">Match Type</h4>
        <b-form-select
          id="server_channel_select"
          v-model="trigger_type"
          value="Please select"
        >
          <option v-for="t in match_mapping" :key="t.id" :value="t.id">
            {{ t.name }}
          </option>
        </b-form-select>
        <hr />

        <!-- Trigger Input - Hide for Role Assignment Triggers -->
        <div v-if="trigger_type !== 8">
          <h4 class="smalltitle">Trigger</h4>
          <b-input-group>
            <b-form-input
              type="text"
              pattern="[A-z0-9-_ ]{1,50}"
              class="col-12"
              v-model="trigger_name"
            ></b-form-input>
          </b-input-group>
          <hr />
        </div>

        <!-- Channel for Role Assignment Triggers (match_type = 8) -->
        <div v-if="trigger_type === 8">
          <h4 class="smalltitle">Target Channel</h4>
          <b-form-select
            v-model="role_assignment_channel"
            value="Please select"
            style="margin-bottom: 15px"
          >
            <option :value="null" disabled>Please select a channel</option>
            <option
              v-for="c in channels.filter((ch) => ch.type === 0)"
              :key="c.id"
              :value="c.id"
            >
              #{{ c.name }}
            </option>
          </b-form-select>
          <hr />
        </div>

        <h4 class="smalltitle">Response</h4>
        <b-input-group>
          <b-form-textarea
            rows="6"
            type="text"
            pattern="[a-z0-9-_]{1,5000}"
            placeholder="Enter some trigger content"
            class="col-12"
            v-model="trigger_content"
          ></b-form-textarea>
        </b-input-group>
        <EmbedBuilder ref="embed_builder_create"></EmbedBuilder>
        <hr />

        <!-- Universal Role Filtering - For ALL trigger types -->

        <div style="margin-bottom: 15px">
          <h5 class="smalltitle">Whitelist Roles</h5>
          <multiselect
            v-model="roles_wl"
            :options="guild_roles"
            :multiple="true"
            label="name"
            track-by="id"
            placeholder="Select roles"
            style="margin-bottom: 10px"
          >
            <template slot="tag" slot-scope="{ option, search, remove }">
              <span
                class="multiselect__tag"
                :style="{
                  'border-color': option
                    ? '#' + option.color.toString(16)
                    : '#eeaaee',
                }"
              >
                <i
                  aria-hidden="true"
                  tabindex="1"
                  @keydown.enter.prevent="remove(option)"
                  @mousedown.prevent="remove(option)"
                  class="multiselect__tag-icon fakediscord"
                  :style="{
                    'background-color': '#' + intToHex(option.color || 0),
                  }"
                ></i>
                <span class="defaultcursor" v-text="option.name"></span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div
                :style="{ color: parseColor(props.option) }"
                class="option__desc"
              >
                <span class="option__title">{{ props.option.name }}</span>
              </div>
            </template>
          </multiselect>

          <h5 class="smalltitle">Blacklist Roles</h5>
          <multiselect
            v-model="roles_bl"
            :options="guild_roles"
            :multiple="true"
            label="name"
            track-by="id"
            placeholder="Select roles"
            style="margin-bottom: 10px"
          >
            <template slot="tag" slot-scope="{ option, search, remove }">
              <span
                class="multiselect__tag"
                :style="{
                  'border-color': option
                    ? '#' + option.color.toString(16)
                    : '#eeaaee',
                }"
              >
                <i
                  aria-hidden="true"
                  tabindex="1"
                  @keydown.enter.prevent="remove(option)"
                  @mousedown.prevent="remove(option)"
                  class="multiselect__tag-icon fakediscord"
                  :style="{
                    'background-color': '#' + intToHex(option.color || 0),
                  }"
                ></i>
                <span class="defaultcursor" v-text="option.name"></span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div
                :style="{ color: parseColor(props.option) }"
                class="option__desc"
              >
                <span class="option__title">{{ props.option.name }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <hr />

        <!-- Universal Channel Filtering - For ALL trigger types -->
        <div v-if="trigger_type !== 3 && trigger_type !== 8" style="margin-bottom: 15px">
          <h5 class="smalltitle">Whitelist Channels</h5>
          <multiselect
            v-model="channels_wl"
            :options="groupedChannels"
            :multiple="true"
            group-values="channels"
            group-label="category"
            :group-select="false"
            label="name"
            track-by="id"
            id="create_channel_wl"
          >
            <template slot="tag" slot-scope="{ option, search, remove }">
              <span
                class="multiselect__tag channelpicker__tag"
                :style="{ borderColor: '#eee' }"
              >
                <i
                  aria-hidden="true"
                  tabindex="1"
                  @keydown.enter.prevent="remove(option)"
                  @mousedown.prevent="remove(option)"
                  class="multiselect__tag-icon channelpicker_icon"
                  :style="{ backgroundColor: '#eee' }"
                ></i>
                <font-awesome-icon
                  style="color: #999; margin-right: 3px"
                  icon="hashtag"
                />
                <span class="defaultcursor" v-text="option.name"></span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="option__title">
                  <font-awesome-icon
                    style="color: #72767d"
                    :icon="
                      !props.option['$isLabel'] ? 'hashtag' : 'chevron-down'
                    "
                  />
                  {{ props.option.name || props.option.$groupLabel.name }}
                </span>
              </div>
            </template>
          </multiselect>

          <h5 class="smalltitle" style="margin-top: 10px">
            Blacklist Channels
          </h5>
          <multiselect
            v-model="channels_bl"
            :options="groupedChannels"
            :multiple="true"
            group-values="channels"
            group-label="category"
            :group-select="false"
            label="name"
            track-by="id"
            id="create_channel_bl"
          >
            <template slot="tag" slot-scope="{ option, search, remove }">
              <span
                class="multiselect__tag channelpicker__tag"
                :style="{ borderColor: '#eee' }"
              >
                <i
                  aria-hidden="true"
                  tabindex="1"
                  @keydown.enter.prevent="remove(option)"
                  @mousedown.prevent="remove(option)"
                  class="multiselect__tag-icon channelpicker_icon"
                  :style="{ backgroundColor: '#eee' }"
                ></i>
                <font-awesome-icon
                  style="color: #999; margin-right: 3px"
                  icon="hashtag"
                />
                <span class="defaultcursor" v-text="option.name"></span>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <span class="option__title">
                  <font-awesome-icon
                    style="color: #72767d"
                    :icon="
                      !props.option['$isLabel'] ? 'hashtag' : 'chevron-down'
                    "
                  />
                  {{ props.option.name || props.option.$groupLabel.name }}
                </span>
              </div>
            </template>
          </multiselect>
          <hr />
        </div>

        <b-form-group>
          <!-- Regex Warning -->
          <b v-if="trigger_type === 7"
            >Please Enter a valid python compatible regex</b
          >

          <!-- Channel Specific (Original) - Only for match_type = 3 -->
          <h5 class="smalltitle" v-if="trigger_type === 3">Specific Channel</h5>
          <b-form-select
            id="server_channel_select"
            v-model="trigger_channel"
            value="Please select"
            v-if="trigger_type === 3"
            style="margin-top: 10px"
          >
            <option v-for="c in channels" :key="c.id" :value="c.id">
              #{{ c.name }}
            </option>
          </b-form-select>

        </b-form-group>
      </b-form-group>
      <div slot="modal-footer" class="w-100">
        <b-container
          fluid
          class="text-light text-center"
          style="padding-right: 0px"
        >
          <b-row class="mr-auto">
            <b-col cols="auto" class="mr-auto p-2"></b-col>
            <b-col cols="auto" class="p-2">
              <b-button
                variant="ghost-secondary"
                v-on:click="createModal = false"
                >Cancel</b-button
              >
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="success" v-on:click="createTrigger"
                >Create</b-button
              >
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import { Switch as cSwitch } from "@coreui/vue";
import { ClientTable, Event } from "vue-tables-2";
import VueNotifications from "vue-notifications";
import miniToastr from "mini-toastr";
import Multiselect from "vue-multiselect";
import EmbedBuilder from "@/views/oc/EmbedBuilder";

const toastTypes = {
  success: "success",
  error: "error",
  info: "info",
  warn: "warn",
};
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
  return miniToastr[type](message, title, timeout, cb);
}

const toast_options = {
  success: toast,
  error: toast,
  info: toast,
  warn: toast,
};
Vue.use(VueNotifications, toast_options);
Vue.use(ClientTable);

export default {
  name: "DataTable",
  components: {
    ClientTable,
    Event,
    cSwitch,
    Multiselect,
    EmbedBuilder,
  },
  notifications: {
    added_trigger: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Trigger created",
    },
    edited_trigger: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Trigger edited",
    },
    deleted_trigger: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Trigger deleted",
    },
    fill_in_fields: {
      type: VueNotifications.types.warn,
      title: "Uh oh",
      message: "Please fill in all fields",
    },
    create_error: {
      type: VueNotifications.types.error,
      title: "Uh oh",
      message: "Something went wrong!",
    },
  },
  data: function () {
    return {
      columns: ["trigger", "response", "type", "actions"],
      data: [],
      limit: 0,
      channels: [],
      guild_roles: [],
      ignored_channels: [],
      matchtypes: [
        "Normal",
        "Strict",
        "Channel specific",
        "Exact",
        "Startswith",
        "Endswith",
        "Regex",
        "Role Assignment", // Updated name for clarity
      ],
      options: {
        filterByColumn: true,
        columnsDisplay: {
          response: "not_mobile",
          type: "not_mobile",
        },
        texts: {
          filter: "Filter",
          filterBy: "Filter by {column}",
        },
        sortable: ["trigger", "response"],
        filterable: ["trigger", "response"],
        sortIcon: {
          base: "fa",
          up: "fa-sort-asc",
          down: "fa-sort-desc",
          is: "fa-sort",
        },
        pagination: {
          chunk: 5,
          edge: false,
          nav: "scroll",
        },
        childRow: false,
        highlightMatches: true,
      },
      useVuex: false,
      theme: "bootstrap4",
      template: "default",
      labelIcon: {
        dataOn: "\u2713",
        dataOff: "\u2715",
      },
      match_mapping: [
        { id: 1, name: "Normal" },
        { id: 2, name: "Strict" },
        { id: 3, name: "Channel specific" },
        { id: 4, name: "Exact" },
        { id: 5, name: "Startswith" },
        { id: 6, name: "Endswith" },
        { id: 7, name: "Regex" },
        { id: 8, name: "Role Assignment" }, // Updated name
      ],
      largeModal: false,
      createModal: false,
      trigger_name: "",
      trigger_content: "",
      trigger_type: 1,
      trigger_channel: null,
      trigger_id: null,
      loading: true,
      // Universal role filtering variables
      role_wl: null,
      role_bl: null,
      roles_wl: [],
      roles_bl: [],
      // Universal channel filtering variables
      channels_wl: [],
      channels_bl: [],
      // Role assignment trigger specific
      role_assignment_channel: null,
    };
  },
  methods: {
    getRoleName(roleId) {
      if (!roleId || !this.guild_roles) return "Unknown Role";
      const role = this.guild_roles.find(
        (r) => String(r.id) === String(roleId),
      );
      return role ? role.name : "Unknown Role";
    },
    getChannelName(channelId) {
      if (!channelId || !this.channels) return "Unknown Channel";
      const channel = this.channels.find(
        (c) => String(c.id) === String(channelId),
      );
      return channel ? channel.name : "Unknown Channel";
    },
    intToHex(int) {
      if (!int) {
        return "eee";
      }
      return int.toString(16);
    },
    parseColor(r) {
      return r.color != null ? "#" + r.color.toString(16) : "#fefefe";
    },
    openEditModal(row) {
      this.trigger_name = row.match_type === 8 ? "" : row.trigger;
      this.trigger_content = row.response.trim();
      this.trigger_type = row.match_type;
      this.trigger_channel = row.specific_channel || null;

      // Universal role filtering - handle arrays
      this.role_wl = row.role_wl || null;
      this.role_bl = row.role_bl || null;

      // Handle new array fields - convert to role objects for multiselect
      this.roles_wl = [];
      if (
        row.roles_wl &&
        Array.isArray(row.roles_wl) &&
        row.roles_wl.length > 0
      ) {
        this.roles_wl = row.roles_wl
          .map((role_id) =>
            this.guild_roles.find((r) => String(r.id) === String(role_id)),
          )
          .filter((r) => r); // Filter out undefined
      }

      this.roles_bl = [];
      if (
        row.roles_bl &&
        Array.isArray(row.roles_bl) &&
        row.roles_bl.length > 0
      ) {
        this.roles_bl = row.roles_bl
          .map((role_id) =>
            this.guild_roles.find((r) => String(r.id) === String(role_id)),
          )
          .filter((r) => r); // Filter out undefined
      }

      // Handle channel whitelist/blacklist arrays
      this.channels_wl = [];
      if (
        row.channel_wl &&
        Array.isArray(row.channel_wl) &&
        row.channel_wl.length > 0
      ) {
        this.channels_wl = row.channel_wl
          .map((channel_id) =>
            this.channels.find((c) => String(c.id) === String(channel_id)),
          )
          .filter((c) => c); // Filter out undefined
      }

      this.channels_bl = [];
      if (
        row.channel_bl &&
        Array.isArray(row.channel_bl) &&
        row.channel_bl.length > 0
      ) {
        this.channels_bl = row.channel_bl
          .map((channel_id) =>
            this.channels.find((c) => String(c.id) === String(channel_id)),
          )
          .filter((c) => c); // Filter out undefined
      }

      // Role assignment trigger specific
      if (row.match_type === 8) {
        this.role_assignment_channel = row.channel || null;
      } else {
        this.role_assignment_channel = null;
      }

      this.largeModal = true;
      var embed_var = JSON.parse(row.embed);
      setTimeout(() => {
        this.$refs.embed_builder_edit.apply_json(embed_var);
        this.$refs.embed_builder_edit.show_embed =
          Object.keys(embed_var).length > 1;
        this.trigger_id = row.id;
        this.$forceUpdate();
      }, 20);
      console.log(row);
      console.log(this.trigger_channel);
    },
    openCreateModal() {
      this.trigger_name = "";
      this.trigger_content = "";
      this.trigger_type = 1;
      this.trigger_channel = null;
      // Reset universal role filtering
      this.role_wl = null;
      this.role_bl = null;
      this.roles_wl = [];
      this.roles_bl = [];
      // Reset universal channel filtering
      this.channels_wl = [];
      this.channels_bl = [];
      // Reset role assignment channel
      this.role_assignment_channel = null;
      this.createModal = true;
      setTimeout(() => {
        this.$refs.embed_builder_create.resetEmbed();
        this.$refs.embed_builder_create.show_embed = false;
      }, 20);
    },
    editTrigger() {
      // Role assignment trigger validation
      if (this.trigger_type === 8) {
        if (
          !this.role_assignment_channel ||
          (!this.trigger_content.trim() &&
            Object.keys(this.$refs.embed_builder_edit.full_json).length < 2)
        ) {
          return this.fill_in_fields();
        }
      } else {
        // Regular trigger validation
        if (
          !this.trigger_name.trim() ||
          (!this.trigger_content.trim() &&
            Object.keys(this.$refs.embed_builder_edit.full_json).length < 2)
        ) {
          return this.fill_in_fields();
        }
        if (this.trigger_type === 3 && !this.trigger_channel) {
          return this.fill_in_fields();
        }
      }

      this.patchTrigger();
    },
    createTrigger() {
      // Role assignment trigger validation
      if (this.trigger_type === 8) {
        if (
          !this.role_assignment_channel ||
          (!this.trigger_content.trim() &&
            Object.keys(this.$refs.embed_builder_create.full_json).length < 2)
        ) {
          return this.fill_in_fields();
        }
      } else {
        // Regular trigger validation
        if (
          !this.trigger_name.trim() ||
          (!this.trigger_content.trim() &&
            Object.keys(this.$refs.embed_builder_create.full_json).length < 2)
        ) {
          return this.fill_in_fields();
        }
        if (this.trigger_name.trim().length < 3) {
          return this.create_error({
            message: "Trigger name cannot be less than 3 characters",
          });
        }
        if (this.trigger_type == 7) {
          try {
            new RegExp(this.trigger_name);
          } catch (e) {
            return this.create_error({
              message: "Please enter a valid regex format",
            });
          }
        }
      }

      this.putTrigger().then((res) => {
        // Refresh the data after creation
        setTimeout(() => this.refreshData(), 1000);
      });
    },
    patchTrigger() {
      // Build request body based on trigger type
      let requestBody;
      if (this.trigger_type === 8) {
        // Role assignment trigger PATCH body
        requestBody = {
          trigger_id: this.trigger_id,
          trigger: "role_trigger",
          response: this.trigger_content,
          match_type: this.trigger_type,
          embed: this.$refs.embed_builder_edit.full_json,
          channel: this.role_assignment_channel,
          roles_wl:
            this.roles_wl.length > 0 ? this.roles_wl.map((r) => r.id) : null,
          roles_bl:
            this.roles_bl.length > 0 ? this.roles_bl.map((r) => r.id) : null,
          channel_wl:
            this.channels_wl.length > 0
              ? this.channels_wl.map((c) => c.id)
              : null,
          channel_bl:
            this.channels_bl.length > 0
              ? this.channels_bl.map((c) => c.id)
              : null,
        };
      } else {
        // Regular/regex trigger PATCH body with universal role and channel filtering
        requestBody = {
          trigger_id: this.trigger_id,
          trigger: this.trigger_name,
          response: this.trigger_content,
          match_type: this.trigger_type,
          specific_channel:
            this.trigger_type === 3 ? this.trigger_channel : null, // sending specific channel only for match_type 3
          embed: this.$refs.embed_builder_edit.full_json,
          roles_wl:
            this.roles_wl.length > 0 ? this.roles_wl.map((r) => r.id) : null,
          roles_bl:
            this.roles_bl.length > 0 ? this.roles_bl.map((r) => r.id) : null,
          channel_wl:
            this.channels_wl.length > 0
              ? this.channels_wl.map((c) => c.id)
              : null,
          channel_bl:
            this.channels_bl.length > 0
              ? this.channels_bl.map((c) => c.id)
              : null,
        };
      }

      this.axios
        .patch(
          "/api/v1/servers/" + this.$route.params.guild_id + "/triggers",
          requestBody,
        )
        .then((r) => {
          if (!r.data.success) {
            this.create_error({ message: r.data.msg });
            return;
          }
          this.edited_trigger();
          this.closeModals();
          // Refresh data after edit
          setTimeout(() => this.refreshData(), 1000);
        });
    },
    deleteTrigger() {
      this.axios
        .delete(
          "/api/v1/servers/" +
            this.$route.params.guild_id +
            "/triggers/" +
            this.trigger_id,
          {
            data: {
              name: this.trigger_name,
            },
          },
        )
        .then(this.closeModals());
      for (let [i, e] of this.data.entries()) {
        if (e.id === this.trigger_id) {
          this.data.splice(i, 1);
        }
      }
      this.deleted_trigger();
    },
    putTrigger() {
      this.closeModals();

      // Build request body based on trigger type
      let requestBody;
      if (this.trigger_type === 8) {
        // Role assignment trigger request body
        requestBody = {
          trigger: "role_trigger",
          response: this.trigger_content,
          match_type: this.trigger_type,
          embed: this.$refs.embed_builder_create.full_json,
          channel: this.role_assignment_channel,
          roles_wl:
            this.roles_wl.length > 0 ? this.roles_wl.map((r) => r.id) : null,
          roles_bl:
            this.roles_bl.length > 0 ? this.roles_bl.map((r) => r.id) : null,
          channel_wl:
            this.channels_wl.length > 0
              ? this.channels_wl.map((c) => c.id)
              : null,
          channel_bl:
            this.channels_bl.length > 0
              ? this.channels_bl.map((c) => c.id)
              : null,
        };
      } else {
        // Regular/regex trigger request body with universal role and channel filtering
        requestBody = {
          trigger: this.trigger_name,
          response: this.trigger_content,
          match_type: this.trigger_type,
          specific_channel: this.trigger_channel,
          embed: this.$refs.embed_builder_create.full_json,
          roles_wl:
            this.roles_wl.length > 0 ? this.roles_wl.map((r) => r.id) : null,
          roles_bl:
            this.roles_bl.length > 0 ? this.roles_bl.map((r) => r.id) : null,
          channel_wl:
            this.channels_wl.length > 0
              ? this.channels_wl.map((c) => c.id)
              : null,
          channel_bl:
            this.channels_bl.length > 0
              ? this.channels_bl.map((c) => c.id)
              : null,
        };
      }

      return this.axios
        .put(
          "/api/v1/servers/" + this.$route.params.guild_id + "/triggers",
          requestBody,
        )
        .then((r) => {
          if (!r.data.success) {
            this.create_error({ message: r.data.msg });
            return;
          }
          this.added_trigger();
          return r.data.id;
        });
    },
    refreshData() {
      this.axios
        .get("/api/v1/servers/" + this.$route.params.guild_id + "/triggers")
        .then((r) => {
          this.data = r.data.triggers;
          this.channels = r.data.channels;
          this.guild_roles = r.data.guild_roles;
          this.ignored_channels =
            this.channels.filter((x) =>
              r.data.ignored_channels.includes(x.id),
            ) || [];
        });
    },
    closeModals() {
      this.largeModal = false;
      this.createModal = false;
    },
  },
  computed: {
    isPremium() {
      return !!(this.$store.state.globalInfo && this.$store.state.globalInfo.isPremium);
    },
    usagePct() {
      if (!this.limit) return 0;
      return Math.round((this.data.length / this.limit) * 100);
    },
    groupedChannels() {
      let groups = [
        {
          category: { id: null, name: "Uncategorized" },
          channels: [],
        },
      ];
      let currentGroup = groups[0];
      for (let chn of this.channels.sort((a, b) => a.position - b.position)) {
        if (chn.type === 4) {
          currentGroup = { category: chn, channels: [] };
          groups.push(currentGroup);
        }
      }
      for (let chn of this.channels.sort((a, b) => a.position - b.position)) {
        if (chn.type === 4) continue;
        currentGroup = groups.find((g) => g.category.id == chn.parent_id);
        if (!currentGroup) continue;
        currentGroup.channels.push(chn);
      }
      return groups;
    },
  },
  beforeRouteUpdate(to, from, next) {
    next();
    this.refreshData();
  },
  created: function () {
    this.$Progress.start();
    this.axios
      .get("/api/v1/servers/" + this.$route.params.guild_id + "/triggers")
      .then((r) => {
        this.data = r.data.triggers;
        this.limit = r.data.limit;
        this.channels = r.data.channels;
        this.guild_roles = r.data.guild_roles;
        this.ignored_channels =
          this.channels.filter((x) => r.data.ignored_channels.includes(x.id)) ||
          [];
        this.loading = false;
        this.$Progress.finish();
      });
  },
};
</script>

<style lang="scss">
#dataTable {
  width: 95%;
  margin: 0 auto;

  .VuePagination {
    text-align: center;
    justify-content: center;
  }

  .td {
    word-wrap: !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vue-title {
    text-align: center;
    margin-bottom: 10px;
  }

  .VueTables__search-field {
    display: flex;
  }
  .VueTables__search-field input {
    margin-left: 0.25rem;
  }

  .VueTables__limit-field {
    display: flex;
  }

  .VueTables__limit-field select {
    margin-left: 0.25rem !important;
  }

  .VueTables__table th {
    text-align: center;
  }

  .VueTables__child-row-toggler {
    width: 16px;
    height: 16px;
    line-height: 16px;
    display: block;
    margin: auto;
    text-align: center;
  }

  .VueTables__child-row-toggler--closed::before {
    content: "+";
  }

  .VueTables__child-row-toggler--open::before {
    content: "-";
  }
}

.usage-chip {
  display: inline-block;
  padding: 2px 10px;
  margin-left: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #d8d6e0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  vertical-align: middle;
}
.usage-chip--warn {
  color: #ffd83d;
  background: rgba(248, 203, 0, 0.12);
  border-color: rgba(248, 203, 0, 0.4);
}
.usage-chip--full {
  color: #fff;
  background: #c97a00;
  border-color: #c97a00;
}

.trigger-upsell {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  margin-bottom: 12px;
  background: rgba(248, 203, 0, 0.08);
  border: 1px solid rgba(248, 203, 0, 0.3);
  border-radius: 10px;
}
.trigger-upsell--full {
  background: rgba(248, 203, 0, 0.16);
  border-color: rgba(248, 203, 0, 0.55);
}
.trigger-upsell__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(248, 203, 0, 0.18);
  color: #f8cb00;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 15px;
}
.trigger-upsell__text {
  flex: 1;
  font-size: 14px;
  color: #d8d6e0;
}
.trigger-upsell__text strong {
  display: block;
  color: #fff;
  margin-bottom: 2px;
}
.trigger-upsell__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f8cb00;
  color: #1a1430;
  font-weight: 600;
  font-size: 13px;
  border-radius: 999px;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}
.trigger-upsell__cta:hover {
  background: #ffd83d;
  color: #1a1430;
  text-decoration: none;
}
.trigger-upsell__cta i {
  font-size: 10px;
}
</style>
